import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useScrollTrigger,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { tokens } from '../theme';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';

const navItems = [
  { label: 'Features', href: '#features' },
  { label: 'Industries', href: '#industries' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleAuthClick = () => {
    if (localStorage.getItem('privacy-policy-accepted') === 'true') {
      window.location.href = 'https://app.cleolead.com/login';
      return;
    }
    setPrivacyDialogOpen(true);
    setMobileOpen(false);
  };

  const handlePrivacyAccept = () => {
    setPrivacyDialogOpen(false);
    window.location.href = 'https://app.cleolead.com/login';
  };

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (href: string) => {
    const hash = href.replace('#', '');

    const tryScroll = (retries = 0) => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else if (retries < 10) {
        setTimeout(() => tryScroll(retries + 1), 100);
      }
    };

    if (location.pathname === '/') {
      tryScroll();
    } else {
      navigate('/');
      setTimeout(() => tryScroll(), 150);
    }
    setMobileOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: trigger ? 'rgba(250, 247, 241, 0.88)' : 'transparent',
          backdropFilter: trigger ? 'blur(12px)' : 'none',
          boxShadow: 'none',
          borderBottom: trigger
            ? `1px solid ${tokens.border}`
            : '1px solid transparent',
          transition: `background-color ${tokens.motion.transition}ms ease, border-color ${tokens.motion.transition}ms ease`,
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 0.8 }}>
            <Box
              component={RouterLink}
              to="/"
              aria-label="CleoLead home"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src="/icon-header-clean.png"
                alt="CleoLead"
                sx={{ height: 30, width: 'auto', display: 'block' }}
              />
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.88rem',
                    px: 2,
                    '&:hover': { color: tokens.copper, bgcolor: 'transparent' },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                onClick={handleAuthClick}
                sx={{
                  ml: 2,
                  color: tokens.ink,
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  '&:hover': { color: tokens.copper, bgcolor: 'transparent' },
                }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAuthClick}
                sx={{
                  fontSize: '0.88rem',
                  px: 2.5,
                  py: 1.1,
                }}
              >
                Start Free Trial
              </Button>
            </Box>

            <IconButton
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, color: tokens.ink }}
            >
              <Menu size={24} strokeWidth={1.5} />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            bgcolor: tokens.bgPaper,
            borderLeft: `1px solid ${tokens.border}`,
            borderRadius: 0,
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              onClick={() => setMobileOpen(false)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src="/icon-header-clean.png"
                alt="CleoLead"
                sx={{ height: 26, width: 'auto', display: 'block' }}
              />
            </Box>
            <IconButton
              aria-label="close drawer"
              onClick={handleDrawerToggle}
              sx={{ color: 'text.secondary' }}
            >
              <X size={22} strokeWidth={1.5} />
            </IconButton>
          </Box>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  onClick={() => scrollToSection(item.href)}
                  sx={{
                    borderRadius: `${tokens.radiusControl}px`,
                    '&:hover': {
                      bgcolor: tokens.copperTint,
                      color: tokens.copper,
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ sx: { fontWeight: 500 } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button variant="outlined" fullWidth onClick={handleAuthClick}>
              Sign In
            </Button>
            <Button variant="contained" color="primary" fullWidth onClick={handleAuthClick}>
              Start Free Trial
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Toolbar />

      <PrivacyPolicyDialog
        open={privacyDialogOpen}
        onClose={() => setPrivacyDialogOpen(false)}
        onAccept={handlePrivacyAccept}
      />
    </>
  );
}
